import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug) || articles[0];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.homes';
  const articleUrl = `${siteUrl}/artykul/${article.slug}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.imageUrl,
    datePublished: '2026-07-01',
    dateModified: '2026-07-21',
    inLanguage: 'pl-PL',
    author: {
      '@type': 'Organization',
      name: 'Kakobuy Spreadsheet Polska',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Kakobuy Spreadsheet Polska',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  return (
    <main className="bg-background-50 min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      {/* Article Nav */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-7 h-7 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-file-list-3-line text-sm text-background-50"></i>
              </div>
              <span className="font-semibold text-sm text-foreground-950 whitespace-nowrap hidden sm:inline">
                Kakobuy Spreadsheet Polska
              </span>
            </Link>
            <div className="flex items-center gap-3 md:gap-4">
              <Link
                to="/artykul/poradnik-zakupowy-kakobuy"
                className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                Poradnik zakupowy
              </Link>
              <Link
                to="/artykul/bezpieczenstwo-kakobuy-poradnik"
                className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                Bezpieczeństwo
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                <span className="hidden sm:inline">Strona główna</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-6">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">Strona główna</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-500 whitespace-nowrap">Artykuły</span>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-700 whitespace-nowrap">{article.category}</span>
        </nav>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-primary-100 text-primary-600 text-xs font-medium rounded-full">{article.category}</span>
            <span className="px-3 py-1 bg-secondary-100 text-secondary-600 text-xs font-medium rounded-full">2026</span>
            <span className="px-3 py-1 bg-accent-100 text-accent-600 text-xs font-medium rounded-full">Polska</span>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            {article.description}
          </p>
        </header>

        {/* Featured Image */}
        <div className="relative w-full h-[220px] sm:h-[280px] md:h-[400px] rounded-2xl overflow-hidden mb-10">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Content */}
        <div className="prose-custom text-sm md:text-base leading-relaxed text-foreground-700 space-y-8">
          {/* Intro */}
          {article.intro && (
            <section>
              <p className="mb-3">{article.intro}</p>
            </section>
          )}

          {/* Dynamic Sections */}
          {article.sections.map((section, idx) => {
            switch (section.type) {
              case 'heading':
                return (
                  <section key={idx}>
                    <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                      {section.title}
                    </h2>
                  </section>
                );

              case 'text':
                return (
                  <section key={idx}>
                    <p className="mb-3">{section.content}</p>
                  </section>
                );

              case 'list':
                return (
                  <section key={idx}>
                    {section.title && (
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                        {section.title}
                      </h2>
                    )}
                    <ul className="space-y-3 mb-4">
                      {section.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 p-3 bg-background-100/60 rounded-lg">
                          <div className="w-6 h-6 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0 mt-0.5">
                            <span className="text-background-50 text-xs font-semibold">{i + 1}</span>
                          </div>
                          <div>
                            <strong className="text-foreground-950 text-sm">{item.title}</strong>
                            <p className="text-sm text-foreground-600 mt-0.5">{item.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                );

              case 'table':
                return (
                  <section key={idx}>
                    {section.title && (
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                        {section.title}
                      </h2>
                    )}
                    {section.content && <p className="mb-4 text-sm">{section.content}</p>}
                    <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            {section.tableHeaders?.map((h, i) => (
                              <th key={i} className="text-left px-4 py-3 font-semibold text-foreground-950 whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.tableRows?.map((row, i) => (
                            <tr key={i} className={`${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'} border-t border-background-200/50`}>
                              {row.map((cell, j) => (
                                <td key={j} className={`px-4 py-3 whitespace-nowrap ${j === 0 ? 'text-foreground-950 font-medium' : 'text-foreground-700'}`}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );

              case 'cards':
                return (
                  <section key={idx}>
                    {section.title && (
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                        {section.title}
                      </h2>
                    )}
                    {section.content && <p className="mb-4 text-sm">{section.content}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {section.cards?.map((card, i) => (
                        <div key={i} className="p-4 bg-background-100/60 rounded-xl border border-background-200/70">
                          <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg mb-3">
                            <i className={`${card.icon} text-accent-500 text-base`}></i>
                          </div>
                          <h3 className="text-sm font-semibold text-foreground-950 mb-1">{card.title}</h3>
                          <p className="text-xs text-foreground-600 leading-relaxed">{card.desc}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                );

              case 'cta':
                return (
                  <section key={idx} className="bg-primary-500/10 rounded-2xl p-6 md:p-8 text-center border border-primary-200/50">
                    <h2 className="text-lg md:text-xl font-semibold text-foreground-950 mb-2">
                      {section.title}
                    </h2>
                    <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
                      {section.content}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href={article.ctaLink}
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all duration-200"
                      >
                        <i className="ri-fire-line"></i>
                        {article.ctaText}
                      </a>
                      <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-8 py-3 bg-background-50 text-foreground-950 rounded-full text-sm font-semibold whitespace-nowrap border border-background-200 hover:border-primary-300 transition-all duration-200"
                      >
                        <i className="ri-home-line"></i>
                        Strona główna
                      </Link>
                    </div>
                  </section>
                );

              default:
                return null;
            }
          })}

          {/* FAQ */}
          {article.faqs.length > 0 && (
            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                Najczęściej zadawane pytania (FAQ)
              </h2>
              <div className="space-y-4">
                {article.faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-100/60 rounded-xl border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-950 list-none">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* CTA at bottom */}
          <section className="bg-primary-500/10 rounded-2xl p-6 md:p-8 text-center border border-primary-200/50">
            <h2 className="text-lg md:text-xl font-semibold text-foreground-950 mb-2">
              Gotowy, aby zacząć?
            </h2>
            <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
              Sprawdź pełną bazę Kakobuy Spreadsheet i znajdź najlepsze produkty już teraz.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all duration-200"
              >
                <i className="ri-fire-line"></i>
                Kakobuy Spreadsheet
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3 bg-background-50 text-foreground-950 rounded-full text-sm font-semibold whitespace-nowrap border border-background-200 hover:border-primary-300 transition-all duration-200"
              >
                <i className="ri-home-line"></i>
                Strona główna
              </Link>
            </div>
          </section>

          {/* Back to home */}
          <div className="text-center pt-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Powrót do strony głównej
            </Link>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="max-w-4xl mx-auto px-4 md:px-6 pb-12">
        <h2 className="text-lg font-semibold text-foreground-950 mb-5 text-center">Powiązane artykuły</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {articles.filter(a => a.slug !== article.slug).slice(0, 3).map((a) => (
            <Link
              key={a.slug}
              to={`/artykul/${a.slug}`}
              className="group block p-4 bg-background-100/60 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-200"
            >
              <span className="inline-block px-2.5 py-0.5 bg-primary-100 text-primary-600 text-xs font-medium rounded-full mb-2">{a.category}</span>
              <h3 className="text-sm font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors line-clamp-2">
                {a.title}
              </h3>
              <p className="text-xs text-foreground-600 line-clamp-2">{a.description}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background-100/80 border-t border-background-200/70 py-8 px-4 md:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors mb-3">
            <i className="ri-home-line"></i>
            Strona główna Kakobuy Spreadsheet Polska
          </Link>
          <p className="text-xs text-foreground-500">
            © 2026 Kakobuy Spreadsheet Polska. Niezależny przewodnik dla użytkowników z Polski.
          </p>
        </div>
      </footer>
    </main>
  );
}